import {
  AssignNodesMaterialItem,
  AssignNodesMaterialRequest,
  Co2MaterialType,
  DensityType,
  MaterialOperationType,
  MaterialType,
  NodeMaterialDto,
  NodeTypes,
  SupplementalType,
} from '@api/apiClient';
import type { INodeDto } from '@/types/INodeDto';
import { GridProvider } from './GridProvider';
import { createNodeMaterialKey, ModelsDataService } from './ModelsDataService';
import { createProgress, ProgressCallback, TASK_IDS } from './TaskProgress';
import { NodeTypes as NumericNodeTypes, TreeRow } from './TreeBuilder';

/**
 * Lookup table for CO2 totals by node.
 * Key format: `${scenarioId}:${nodeId}`
 * Works for all node types: Element, Component, Rebar Supplemental, NonMod Supplemental, etc.
 */
export type NodeMaterialLookup = Map<string, NodeMaterialDto>;

/**
 * Generates a composite key for the CO2 totals lookup.
 * Delegates to createNodeMaterialKey for consistency.
 * @param scenarioId Scenario ID
 * @param nodeId Node ID
 * @returns Composite key in format `${scenarioId}:${nodeId}`
 */
export function createCo2LookupKey(scenarioId: string, nodeId: bigint | number): string {
  return createNodeMaterialKey(scenarioId, nodeId);
}

/**
 * LCA Stage types for carbon calculations.
 * Mirrors the server-side LCAStageType enum.
 */
export enum LCAStageType {
  Default = 'Default',
  A1A2A3 = 'A1A2A3',
  A4 = 'A4',
  A5 = 'A5',
  B1 = 'B1',
  B2 = 'B2',
  B3 = 'B3',
  B4 = 'B4',
  B5 = 'B5',
  B6 = 'B6',
  B7 = 'B7',
  C1 = 'C1',
  C2 = 'C2',
  C3 = 'C3',
  C4 = 'C4',
  D = 'D',
  Biogenic = 'Biogenic',
}

/**
 * Immutable snapshot of materials state for undo/redo.
 */
export interface MaterialAssignmentMemento {
  readonly timestamp: number;
  readonly description: string;
  readonly affectedNodeIds: string[];
  readonly materials: ReadonlyMap<string, NodeMaterialDto>;
}

/**
 * Result of change detection.
 */
export interface MaterialChanges {
  added: NodeMaterialDto[];
  updated: NodeMaterialDto[];
  removed: string[];
}

/**
 * Aggregated metrics for selected elements.
 * All values are sums across the selected nodes.
 */
export interface ElementMetrics {
  /** Total length in meters */
  length: number;
  /** Total area in square meters */
  area: number;
  /** Total volume in cubic meters */
  volume: number;
  /** Total quantity (count) */
  quantity: number;
  /** Total embodied carbon in tCO2e */
  co2eTotal: number;
  /** Number of nodes included in the metrics */
  nodeCount: number;
}

// Re-export TaskProgress types for convenience
export { createProgress, TASK_IDS, type ProgressCallback, type TaskProgress } from './TaskProgress';

/**
 * Generates a composite key for tracking materials.
 * Delegates to createNodeMaterialKey from ModelsDataService for consistency.
 * @param scenarioId Scenario ID
 * @param nodeId Node ID
 */
function createMaterialKey(scenarioId: string, nodeId: bigint | number): string {
  return createNodeMaterialKey(scenarioId, nodeId);
}

/**
 * Maps TreeRow type to MaterialType.
 * @param rowType The TreeRow type
 * @returns The corresponding MaterialType
 */
function getMaterialTypeFromRowType(rowType: TreeRow['type']): MaterialType | undefined {
  switch (rowType) {
    case 'element':
      return MaterialType.Element;
    case 'component':
      return MaterialType.Component;
    case 'supplemental':
      return MaterialType.PrimarySupplemental;
    default:
      return undefined;
  }
}

/**
 * Service for computing embodied carbon (Co2e) values for selected nodes.
 * Supports multi-model merged data, change tracking, and undo/redo via the memento pattern.
 * 
 * Data storage is delegated to ModelsDataService - this service focuses on:
 * - Material assignment logic and CO2 calculations
 * - Undo/redo via memento pattern
 * - Grid integration via TreeGridProvider
 */
export class LCAoMService {
  private gridProvider: GridProvider;
  private modelsDataService: ModelsDataService;
  private undoStack: MaterialAssignmentMemento[];
  private redoStack: MaterialAssignmentMemento[];
  private maxHistorySize: number;

  /**
   * Version counter that increments on every material mutation.
   * Use this as a React dependency to trigger re-renders when materials change.
   * Delegates to the modelsDataService version.
   */
  get version(): number {
    return this.modelsDataService.version;
  }

  /**
   * Increments the version counter to signal a material change.
   * Called internally after any mutation.
   */
  private incrementVersion(): void {
    this.modelsDataService.incrementVersion();
  }

  /**
   * Creates a new LCAoMService.
   * @param gridProvider TreeGridProvider for accessing grid data
   * @param modelsDataService ModelsDataService for centralized data storage
   * @param maxHistorySize Maximum number of undo states to keep (default: 50)
   */
  constructor(gridProvider: GridProvider, modelsDataService: ModelsDataService, maxHistorySize: number = 50) {
    this.gridProvider = gridProvider;
    this.modelsDataService = modelsDataService;
    this.undoStack = [];
    this.redoStack = [];
    this.maxHistorySize = maxHistorySize;
  }

  // ============================================================================
  // Core Assignment Methods
  // ============================================================================

  /**
   * Assigns a material to the currently selected rows.
   * Computes Co2e values client-side and marks rows as changed.
   * Preserves existing server-side fields (nodeMaterialId, createdAt, etc.) when updating.
   * Uses chunked processing for large datasets to keep UI responsive.
   * 
   * Note: Supplemental rows that are not of type Primary are skipped - those material
   * assignments are done on the backend only.
   * 
   * @param co2MaterialType The material type to assign
   * @param scenarioId The scenario ID
   * @param onProgress Optional progress callback for UI updates
   * @returns Array of computed NodeMaterialDto objects
   */
  async assignMaterialToSelection(
    co2MaterialType: Co2MaterialType,
    scenarioId: string,
    onProgress?: ProgressCallback
  ): Promise<NodeMaterialDto[]> {
    const selectedRows = this.gridProvider.getSelectedAssignableRows();
    if (selectedRows.length === 0) {
      return [];
    }

    const totalRows = selectedRows.length;

    // Save memento before mutation
    const description = `Assigned ${co2MaterialType.name} to ${totalRows} items`;
    const affectedNodeIds = selectedRows.map((row) => row.id);
    this.saveMemento(description, affectedNodeIds);

    const results: NodeMaterialDto[] = [];

    console.log(`[LCAoMService:assignMaterialToSelection] Processing ${totalRows} rows for scenarioId: ${scenarioId}`);
    console.log(`[LCAoMService:assignMaterialToSelection] nodeMaterials.size before: ${this.modelsDataService.nodeMaterials.size}`);

    onProgress?.(createProgress(
      TASK_IDS.ASSIGN_MATERIALS,
      'Assigning materials...',
      'processing',
      0,
      totalRows,
      'rows'
    ));

    // Chunked processing - yield every ~50ms to keep UI responsive
    const CHUNK_SIZE = 1000; // Process 1000 rows per chunk
    let lastYield = performance.now();

    // Get supplementals from data service for filtering
    const nodeSupplementals = this.modelsDataService.nodeSupplementals;

    for (let i = 0; i < selectedRows.length; i++) {
      const row = selectedRows[i];
      // Leaf nodes will be Elements without child components or Components or Supplementals.
      // If an Element or Component node has supplemental children, it is no longer a leaf node.
      // Nodes with a co2MaterialTypeSpec are handled in auto-assign on the backend.
      if (!row.leaf || row.childCount > 0 || !row.data || row.data.co2MaterialTypeSpec) continue;

      const node = row.data;
      const modelId = node.modelId;
      const nodeId = node.nodeId;
      if (modelId == null || nodeId === undefined || nodeId === null) continue;

      // Skip supplemental rows that are not of type Primary - those are handled by the backend only
      // Check both row.type (string: 'supplemental') and node.nodeType (number: 6 = Supplemental)
      if (row.type === 'supplemental' || node.nodeType === NumericNodeTypes.Supplemental) {
        const supplemental = nodeSupplementals.get(Number(nodeId));
        if (supplemental?.type !== SupplementalType.Primary) {
          continue;
        }
      }

      const key = createMaterialKey(scenarioId, nodeId);

      // Check if material already exists (for updates)
      const existingMaterial = this.modelsDataService.getNodeMaterial(key);

      const material = this.computeMaterialForNode(
        node,
        co2MaterialType,
        modelId,
        scenarioId,
        row.type,
        existingMaterial
      );

      if (material) {
        this.modelsDataService.setNodeMaterial(key, material);
        results.push(material);
      }

      // Yield to UI every CHUNK_SIZE items or every 50ms
      if ((i + 1) % CHUNK_SIZE === 0) {
        const now = performance.now();
        if (now - lastYield > 50) {
          // Update progress
          onProgress?.(createProgress(
            TASK_IDS.ASSIGN_MATERIALS,
            `Processing ${(i + 1).toLocaleString()} of ${totalRows.toLocaleString()}...`,
            'processing',
            i + 1,
            totalRows,
            'elements'
          ));
          // Yield to allow UI updates
          await new Promise(resolve => setTimeout(resolve, 0));
          lastYield = performance.now();
        }
      }
    }

    console.log(`[LCAoMService:assignMaterialToSelection] nodeMaterials.size after: ${this.modelsDataService.nodeMaterials.size}, added: ${results.length}`);
    console.log(`[LCAoMService:assignMaterialToSelection] Sample keys:`, Array.from(this.modelsDataService.nodeMaterials.keys()).slice(0, 3));

    // Mark rows as changed in the grid
    this.gridProvider.refreshRows();

    onProgress?.(createProgress(
      TASK_IDS.ASSIGN_MATERIALS,
      'Complete',
      'complete',
      totalRows,
      totalRows
    ));

    this.incrementVersion();
    return results;
  }

  /**
   * Removes material assignments from the currently selected rows.
   * @param scenarioId The scenario ID
   */
  removeMaterialFromSelection(scenarioId: string): void {
    const selectedRows = this.gridProvider.getSelectedAssignableRows();
    if (selectedRows.length === 0) {
      return;
    }

    // Save memento before mutation
    const description = `Removed materials from ${selectedRows.length} items`;
    const affectedNodeIds = selectedRows.map((row) => row.id);
    this.saveMemento(description, affectedNodeIds);

    for (const row of selectedRows) {
      if (!row.data || row.data.nodeId == null) continue;

      const node = row.data;
      const key = createMaterialKey(scenarioId, node.nodeId!);
      this.modelsDataService.deleteNodeMaterial(key);
    }

    // Clear change markers in the grid
    this.gridProvider.clearRowChanges(affectedNodeIds);
    this.incrementVersion();
  }

  // ============================================================================
  // Calculation Methods (mirrors server ComputetCO2eForNode)
  // ============================================================================

  /**
   * Computes a NodeMaterialDto for a node with the given material type.
   * If an existing material is provided, preserves server-side fields (nodeMaterialId, createdAt, etc.).
   * @param node The node to compute material for
   * @param materialType The material type to assign
   * @param modelId The model ID
   * @param scenarioId The scenario ID
   * @param rowType The row type
   * @param existingMaterial Optional existing material to update (preserves server-side fields)
   */
  private computeMaterialForNode(
    node: INodeDto,
    materialType: Co2MaterialType,
    modelId: string,
    scenarioId: string,
    rowType: TreeRow['type'],
    existingMaterial?: NodeMaterialDto
  ): NodeMaterialDto | null {
    // If updating existing material, preserve server-side fields
    const material = existingMaterial
      ? new NodeMaterialDto({
        ...existingMaterial,
        co2MaterialTypeId: materialType.co2MaterialTypeId,
        cadModelCategoryId: node.cadModelCategoryId ?? undefined,
        type: getMaterialTypeFromRowType(rowType),
      })
      : new NodeMaterialDto({
        modelId,
        scenarioId,
        nodeId: Number(node.nodeId!),
        co2MaterialTypeId: materialType.co2MaterialTypeId,
        cadModelCategoryId: node.cadModelCategoryId ?? undefined,
        type: getMaterialTypeFromRowType(rowType),
      });

    // Calculate CO2 values for each LCA stage
    material.a1A2A3 = this.computeCo2eForNode(node, materialType, LCAStageType.A1A2A3);
    material.a4 = this.computeCo2eForNode(node, materialType, LCAStageType.A4);
    material.a5 = this.computeCo2eForNode(node, materialType, LCAStageType.A5);
    material.b1 = this.computeCo2eForNode(node, materialType, LCAStageType.B1);
    material.b2 = this.computeCo2eForNode(node, materialType, LCAStageType.B2);
    material.b3 = this.computeCo2eForNode(node, materialType, LCAStageType.B3);
    material.b4 = this.computeCo2eForNode(node, materialType, LCAStageType.B4);
    material.b5 = this.computeCo2eForNode(node, materialType, LCAStageType.B5);
    material.b6 = this.computeCo2eForNode(node, materialType, LCAStageType.B6);
    material.b7 = this.computeCo2eForNode(node, materialType, LCAStageType.B7);
    material.c1 = this.computeCo2eForNode(node, materialType, LCAStageType.C1);
    material.c2 = this.computeCo2eForNode(node, materialType, LCAStageType.C2);
    material.c3 = this.computeCo2eForNode(node, materialType, LCAStageType.C3);
    material.c4 = this.computeCo2eForNode(node, materialType, LCAStageType.C4);
    material.d = this.computeCo2eForNode(node, materialType, LCAStageType.D);
    material.biogenic = this.computeCo2eForNode(node, materialType, LCAStageType.Biogenic);

    // Compute total
    material.totalCo2 = this.computeTotalCo2(material);

    return material;
  }

  /**
   * Computes the CO2e value for a specific LCA stage.
   * Mirrors the server-side ComputetCO2eForNode method.
   * 
   * Formula: quantity_value * carbonImpactValue * impactFactor / 1000
   * 
   * Unit determines quantity source:
   * - kg/ton: densityType selects area*density or volume*density  
   * - m3: volume
   * - m2/m2 RSI: area
   * - m: length
   * - item/absolute: quantity
   */
  private computeCo2eForNode(
    node: INodeDto,
    materialType: Co2MaterialType,
    lcaStageType: LCAStageType
  ): number | undefined {
    if (!materialType) {
      return undefined;
    }

    const impactFactor = this.getImpactFactor(materialType, lcaStageType);
    if (impactFactor === undefined || impactFactor === null) {
      return undefined;
    }

    const carbonImpactValue = node.carbonImpactValue || 1;
    const quantity = this.getQuantityForUnit(node, materialType);

    if (quantity === null || quantity === undefined) {
      return undefined;
    }

    return (quantity * carbonImpactValue * impactFactor) / 1000;
  }

  /**
   * Gets the quantity value based on the material type's unit.
   */
  private getQuantityForUnit(node: INodeDto, materialType: Co2MaterialType): number | null {
    const unit = materialType.unit?.toLowerCase() || '';

    if (unit === 'kg' || unit === 'ton') {
      const density = materialType.density ?? 0;
      switch (materialType.densityType) {
        case DensityType.Area:
          return (node.area ?? 0) * density;
        case DensityType.Volume:
          return (node.volume ?? 0) * density;
        default:
          console.warn(`Unsupported density type ${materialType.densityType} for material type`);
          return null;
      }
    } else if (unit === 'm3') {
      return node.volume ?? null;
    } else if (unit === 'm2' || unit === 'm2 rsi') {
      return node.area ?? null;
    } else if (unit === 'm') {
      return node.length ?? null;
    } else if (unit === 'item' || unit === 'absolute') {
      return node.quantity ?? null;
    }

    console.warn(`Unsupported units ${materialType.unit} for material type`);
    return null;
  }

  /**
   * Static map of LCA stage to Co2MaterialType property.
   * Cached to avoid recreation on every call.
   */
  private static readonly STAGE_PROPERTY_MAP: Record<LCAStageType, keyof Co2MaterialType | null> = {
    [LCAStageType.Default]: null,
    [LCAStageType.A1A2A3]: 'a1A2A3',
    [LCAStageType.A4]: 'a4',
    [LCAStageType.A5]: 'a5',
    [LCAStageType.B1]: 'b1',
    [LCAStageType.B2]: 'b2',
    [LCAStageType.B3]: 'b3',
    [LCAStageType.B4]: 'b4',
    [LCAStageType.B5]: 'b5',
    [LCAStageType.B6]: 'b6',
    [LCAStageType.B7]: 'b7',
    [LCAStageType.C1]: 'c1',
    [LCAStageType.C2]: 'c2',
    [LCAStageType.C3]: 'c3',
    [LCAStageType.C4]: 'c4',
    [LCAStageType.D]: 'd',
    [LCAStageType.Biogenic]: 'biogenic',
  };

  /**
   * Gets the impact factor for a specific LCA stage from the material type.
   */
  private getImpactFactor(
    materialType: Co2MaterialType,
    lcaStageType: LCAStageType
  ): number | undefined {
    if (!materialType) {
      return 0;
    }

    const propertyName = LCAoMService.STAGE_PROPERTY_MAP[lcaStageType];
    if (!propertyName) {
      return undefined;
    }

    const value = materialType[propertyName];
    return typeof value === 'number' ? value : undefined;
  }

  /**
   * Computes the total CO2 by summing all stage values.
   * Returns undefined if all stage values are null/undefined.
   * Single-pass, zero allocations.
   */
  private computeTotalCo2(material: Partial<NodeMaterialDto>): number | undefined {
    let sum = 0;
    let hasValue = false;

    if (material.a1A2A3 != null) { sum += material.a1A2A3; hasValue = true; }
    if (material.a4 != null) { sum += material.a4; hasValue = true; }
    if (material.a5 != null) { sum += material.a5; hasValue = true; }
    if (material.b1 != null) { sum += material.b1; hasValue = true; }
    if (material.b2 != null) { sum += material.b2; hasValue = true; }
    if (material.b3 != null) { sum += material.b3; hasValue = true; }
    if (material.b4 != null) { sum += material.b4; hasValue = true; }
    if (material.b5 != null) { sum += material.b5; hasValue = true; }
    if (material.b6 != null) { sum += material.b6; hasValue = true; }
    if (material.b7 != null) { sum += material.b7; hasValue = true; }
    if (material.c1 != null) { sum += material.c1; hasValue = true; }
    if (material.c2 != null) { sum += material.c2; hasValue = true; }
    if (material.c3 != null) { sum += material.c3; hasValue = true; }
    if (material.c4 != null) { sum += material.c4; hasValue = true; }
    if (material.d != null) { sum += material.d; hasValue = true; }
    if (material.biogenic != null) { sum += material.biogenic; hasValue = true; }

    return hasValue ? sum : undefined;
  }

  // ============================================================================
  // Change Tracking Methods
  // ============================================================================

  /**
   * Helper method to create AssignNodesMaterialItem from NodeMaterialDto.
   * Reduces code duplication in generatePatchRequest.
   */
  private createMaterialItem(
    material: NodeMaterialDto,
    operationType: MaterialOperationType
  ): AssignNodesMaterialItem {
    const nodeType = this.getNodeTypeFromMaterialType(material.type as MaterialType | undefined);
    return new AssignNodesMaterialItem({
      operationType,
      nodeMaterialId: material.nodeMaterialId,
      modelId: material.modelId,
      scenarioId: material.scenarioId,
      nodeId: material.nodeId,
      nodeType,
      co2MaterialTypeId: material.co2MaterialTypeId,
      cadModelCategoryId: material.cadModelCategoryId,
      type: material.type as MaterialType | undefined,
      a1A2A3: material.a1A2A3,
      a4: material.a4,
      a5: material.a5,
      b1: material.b1,
      b2: material.b2,
      b3: material.b3,
      b4: material.b4,
      b5: material.b5,
      b6: material.b6,
      b7: material.b7,
      c1: material.c1,
      c2: material.c2,
      c3: material.c3,
      c4: material.c4,
      d: material.d,
      biogenic: material.biogenic,
      totalCo2: material.totalCo2
    });
  }

  /**
   * Generates a patch request for all changes since the original state.
   * Single request can contain items from multiple models.
   * Optimized to reduce code duplication.
   * @param scenarioId The scenario ID
   */
  generatePatchRequest(scenarioId: string): AssignNodesMaterialRequest {
    const changes = this.getChanges(scenarioId);
    const items: AssignNodesMaterialItem[] = [];

    // Pre-allocate array capacity for better performance
    const totalItems = changes.added.length + changes.updated.length + changes.removed.length;
    items.length = 0; // Clear but maintain reference

    // Add insert operations
    for (const material of changes.added) {
      items.push(this.createMaterialItem(material, MaterialOperationType.Insert));
    }

    // Add update operations
    for (const material of changes.updated) {
      items.push(this.createMaterialItem(material, MaterialOperationType.Update));
    }

    // Add delete operations
    for (const nodeMaterialId of changes.removed) {
      items.push(
        new AssignNodesMaterialItem({
          operationType: MaterialOperationType.Delete,
          nodeMaterialId,
        })
      );
    }

    return new AssignNodesMaterialRequest({ materials: items });
  }

  /**
   * Gets the changes between current and original materials.
   * Optimized to use single pass through both maps and avoid string operations in loop.
   * @param scenarioId The scenario ID to filter by
   */
  getChanges(scenarioId: string): MaterialChanges {
    const added: NodeMaterialDto[] = [];
    const updated: NodeMaterialDto[] = [];
    const removed: string[] = [];

    // Key format is `${scenarioId}:${nodeId}` - optimize prefix checking
    const keyPrefix = `${scenarioId}:`;
    const prefixLength = keyPrefix.length;

    const nodeMaterials = this.modelsDataService.nodeMaterials;
    const originalNodeMaterials = this.modelsDataService.originalNodeMaterials;

    // Debug: Log sizes
    console.log(`[LCAoMService:getChanges] Checking changes for scenarioId: ${scenarioId}`);
    console.log(`[LCAoMService:getChanges] nodeMaterials.size: ${nodeMaterials.size}, originalNodeMaterials.size: ${originalNodeMaterials.size}`);

    // Track which original materials we've seen to detect removals efficiently
    const seenOriginalKeys = new Set<string>();

    // Single pass: Check for added/updated
    let matchingMaterials = 0;
    for (const [key, material] of nodeMaterials) {
      // Optimize: check prefix length first, then compare
      if (key.length < prefixLength || key.substring(0, prefixLength) !== keyPrefix) continue;
      matchingMaterials++;

      const original = originalNodeMaterials.get(key);
      if (!original) {
        added.push(material);
      } else {
        seenOriginalKeys.add(key);
        if (this.hasMaterialChanged(original, material)) {
          updated.push(material);
        }
      }
    }
    console.log(`[LCAoMService:getChanges] Materials matching prefix: ${matchingMaterials}`);

    // Single pass: Check for removed (only iterate originals with matching prefix)
    for (const [key, original] of originalNodeMaterials) {
      // Optimize: check prefix length first, then compare
      if (key.length < prefixLength || key.substring(0, prefixLength) !== keyPrefix) continue;

      if (!seenOriginalKeys.has(key) && original.nodeMaterialId) {
        removed.push(original.nodeMaterialId);
      }
    }

    console.log(`[LCAoMService:getChanges] Result: added=${added.length}, updated=${updated.length}, removed=${removed.length}`);
    return { added, updated, removed };
  }

  /**
   * Checks if there are any unsaved changes for a scenario.
   * Delegates to modelsDataService for the check.
   * @param scenarioId The scenario ID
   */
  hasChanges(scenarioId: string): boolean {
    return this.modelsDataService.hasChanges(scenarioId);
  }

  /**
   * Checks if there are any local modifications (any scenario).
   * Used to prevent re-hydration from wiping unsaved changes.
   * Delegates to modelsDataService.
   */
  hasLocalChanges(): boolean {
    return this.modelsDataService.hasLocalChanges();
  }

  /**
   * Compares two materials to check if they have changed.
   */
  private hasMaterialChanged(original: NodeMaterialDto, current: NodeMaterialDto): boolean {
    return (
      original.co2MaterialTypeId !== current.co2MaterialTypeId ||
      original.a1A2A3 !== current.a1A2A3 ||
      original.a4 !== current.a4 ||
      original.a5 !== current.a5 ||
      original.b1 !== current.b1 ||
      original.b2 !== current.b2 ||
      original.b3 !== current.b3 ||
      original.b4 !== current.b4 ||
      original.b5 !== current.b5 ||
      original.b6 !== current.b6 ||
      original.b7 !== current.b7 ||
      original.c1 !== current.c1 ||
      original.c2 !== current.c2 ||
      original.c3 !== current.c3 ||
      original.c4 !== current.c4 ||
      original.d !== current.d ||
      original.biogenic !== current.biogenic
    );
  }

  /**
   * Maps MaterialType to NodeTypes.
   */
  private getNodeTypeFromMaterialType(materialType: MaterialType | undefined): NodeTypes | undefined {
    switch (materialType) {
      case MaterialType.Element:
        return NodeTypes.Element;
      case MaterialType.Component:
        return NodeTypes.Component;
      case MaterialType.PrimarySupplemental:
      case MaterialType.RebarSupplemental:
      case MaterialType.NonModelledSupplemental:
      case MaterialType.AdhocSupplemental:
        return NodeTypes.Supplemental;
      default:
        return undefined;
    }
  }

  // ============================================================================
  // Undo/Redo Methods (Memento Pattern)
  // ============================================================================

  /**
   * Undoes the last material assignment operation.
   * @returns The restored memento or null if nothing to undo
   */
  undo(): MaterialAssignmentMemento | null {
    if (!this.canUndo()) {
      return null;
    }

    // Save current state to redo stack
    const currentMemento = this.createMemento('Current state', []);
    this.redoStack.push(currentMemento);

    // Restore from undo stack
    const memento = this.undoStack.pop()!;
    this.restoreMemento(memento);

    return memento;
  }

  /**
   * Redoes the last undone operation.
   * @returns The restored memento or null if nothing to redo
   */
  redo(): MaterialAssignmentMemento | null {
    if (!this.canRedo()) {
      return null;
    }

    // Save current state to undo stack
    const currentMemento = this.createMemento('Current state', []);
    this.undoStack.push(currentMemento);

    // Restore from redo stack
    const memento = this.redoStack.pop()!;
    this.restoreMemento(memento);

    return memento;
  }

  /**
   * Checks if undo is available.
   */
  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /**
   * Checks if redo is available.
   */
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /**
   * Gets a description of the next undo operation.
   */
  getUndoDescription(): string | null {
    if (!this.canUndo()) {
      return null;
    }
    return `Undo: ${this.undoStack[this.undoStack.length - 1].description}`;
  }

  /**
   * Gets a description of the next redo operation.
   */
  getRedoDescription(): string | null {
    if (!this.canRedo()) {
      return null;
    }
    return `Redo: ${this.redoStack[this.redoStack.length - 1].description}`;
  }

  /**
   * Clears the undo/redo history.
   */
  clearHistory(): void {
    this.undoStack = [];
    this.redoStack = [];
  }

  /**
   * Saves a memento before mutation.
   */
  private saveMemento(description: string, affectedNodeIds: string[]): void {
    const memento = this.createMemento(description, affectedNodeIds);
    this.undoStack.push(memento);

    // Limit history size
    while (this.undoStack.length > this.maxHistorySize) {
      this.undoStack.shift();
    }

    // Clear redo stack on new mutation
    this.redoStack = [];
  }

  /**
   * Creates a memento from current state.
   */
  private createMemento(description: string, affectedNodeIds: string[]): MaterialAssignmentMemento {
    return {
      timestamp: Date.now(),
      description,
      affectedNodeIds,
      materials: this.modelsDataService.cloneNodeMaterials(),
    };
  }

  /**
   * Restores state from a memento.
   */
  private restoreMemento(memento: MaterialAssignmentMemento): void {
    this.modelsDataService.restoreNodeMaterials(new Map(memento.materials));

    // Update grid rows for affected nodes
    if (memento.affectedNodeIds.length > 0) {
      this.gridProvider.clearRowChanges(memento.affectedNodeIds);
    }
    // Note: restoreNodeMaterials already increments version
  }

  // ============================================================================
  // State Management Methods
  // ============================================================================

  /**
   * Loads existing materials from a Map (more efficient than array).
   * Delegates to modelsDataService for storage and clears undo/redo history.
   * @param materialsMap Map of nodeId -> NodeMaterialDto
   * @param scenarioId Scenario ID for composite key generation
   * @deprecated Use modelsDataService.loadOriginalNodeMaterials() directly instead
   */
  loadExistingMaterials(materialsMap: Map<number, NodeMaterialDto>, scenarioId: string): void {
    this.modelsDataService.loadOriginalNodeMaterials(materialsMap, scenarioId);
    this.clearHistory();
  }

  /**
   * Gets the material for a specific node.
   * @param nodeId The node ID
   * @param scenarioId The scenario ID
   */
  getMaterialForNode(
    nodeId: bigint | number,
    scenarioId: string,
  ): NodeMaterialDto | undefined {
    const key = createMaterialKey(scenarioId, nodeId);
    return this.modelsDataService.getNodeMaterial(key);
  }

  /**
   * Clears all materials and history.
   */
  clear(): void {
    this.modelsDataService.clearNodeMaterials();
    this.clearHistory();
  }

  /**
   * Gets all current materials.
   */
  getMaterials(): Map<string, NodeMaterialDto> {
    return new Map(this.modelsDataService.nodeMaterials);
  }

  /**
   * Gets the node material lookup table for use in grid columns.
   * Returns the nodeMaterials map from modelsDataService directly for performance.
   * The map uses composite keys in format `${scenarioId}:${nodeId}`.
   * @returns The node material lookup table
   */
  getNodeMaterialLookup(): NodeMaterialLookup {
    return this.modelsDataService.nodeMaterials;
  }

  /**
   * Gets the count of materials for a scenario.
   * Delegates to modelsDataService.
   * @param scenarioId The scenario ID
   */
  getMaterialCount(scenarioId: string): number {
    return this.modelsDataService.getNodeMaterialCount(scenarioId);
  }

  /**
   * Gets changed materials grouped by modelId for IndexedDB updates.
   * Returns only added and updated materials (not removed ones).
   * Optimized to avoid creating intermediate arrays and use Set for filtering.
   * @param scenarioId The scenario ID
   * @param modelIds List of model IDs to filter by (optional - if empty, returns all models)
   * @returns Record of modelId to array of changed materials
   */
  getChangedMaterialsByModel(
    scenarioId: string,
    modelIds?: string[]
  ): Record<string, NodeMaterialDto[]> {
    const changes = this.getChanges(scenarioId);
    const result: Record<string, NodeMaterialDto[]> = {};

    // Use Set for O(1) lookup if modelIds filter is provided
    const modelIdSet = modelIds && modelIds.length > 0 ? new Set(modelIds) : null;

    // Process added materials
    for (const material of changes.added) {
      if (!material.modelId) continue;
      if (modelIdSet && !modelIdSet.has(material.modelId)) continue;

      const modelId = material.modelId;
      if (!result[modelId]) {
        result[modelId] = [];
      }
      result[modelId].push(material);
    }

    // Process updated materials
    for (const material of changes.updated) {
      if (!material.modelId) continue;
      if (modelIdSet && !modelIdSet.has(material.modelId)) continue;

      const modelId = material.modelId;
      if (!result[modelId]) {
        result[modelId] = [];
      }
      result[modelId].push(material);
    }

    return result;
  }

  /**
   * Gets all materials for a scenario grouped by modelId.
   * Useful for full IndexedDB synchronization.
   * Optimized to avoid string operations in loop.
   * @param scenarioId The scenario ID
   * @returns Record of modelId to array of all materials
   */
  getMaterialsByModel(scenarioId: string): Record<string, NodeMaterialDto[]> {
    const keyPrefix = `${scenarioId}:`;
    const prefixLength = keyPrefix.length;
    const result: Record<string, NodeMaterialDto[]> = {};

    for (const [key, material] of this.modelsDataService.nodeMaterials) {
      // Optimize: check prefix length first, then compare
      if (key.length < prefixLength || key.substring(0, prefixLength) !== keyPrefix) continue;
      if (!material.modelId) continue;

      const modelId = material.modelId;
      if (!result[modelId]) {
        result[modelId] = [];
      }
      result[modelId].push(material);
    }

    return result;
  }

  // ============================================================================
  // Metrics Computation Methods
  // ============================================================================

  /**
   * Computes aggregated metrics for the currently selected nodes in the grid.
   * Aggregates length, area, volume, quantity from TreeRow.data (NodeDto),
   * and co2eTotal from the nodeMaterialLookup.
   * @param scenarioId The scenario ID for looking up CO2 totals
   * @returns ElementMetrics with aggregated values for the selection
   */
  getSelectedElementMetrics(scenarioId: string): ElementMetrics {
    const selectedRows = this.gridProvider.getSelectedAssignableRows();
    const nodeMaterialLookup = this.getNodeMaterialLookup();

    let length = 0;
    let area = 0;
    let volume = 0;
    let quantity = 0;
    let co2eTotal = 0;
    let nodeCount = 0;

    for (const row of selectedRows) {
      const data = row.data;
      if (!data) continue;

      nodeCount++;

      // Aggregate physical dimensions from NodeDto
      if (data.length !== null && data.length !== undefined) {
        length += data.length;
      }
      if (data.area !== null && data.area !== undefined) {
        area += data.area;
      }
      if (data.volume !== null && data.volume !== undefined) {
        volume += data.volume;
      }
      if (data.quantity !== null && data.quantity !== undefined) {
        quantity += data.quantity;
      }

      // Get CO2 total from the lookup
      if (data.nodeId != null) {
        const key = createCo2LookupKey(scenarioId, data.nodeId);
        const material = nodeMaterialLookup.get(key);
        if (material?.totalCo2 !== null && material?.totalCo2 !== undefined) {
          co2eTotal += material.totalCo2;
        }
      }
    }

    return {
      length,
      area,
      volume,
      quantity,
      co2eTotal,
      nodeCount,
    };
  }
}
